import { useState } from "react";
import { Link } from "react-router-dom";
import NetworkGraph from "../../components/NetworkGraph";
import Reveal from "../../components/Reveal";
import { IconMonitor, IconCheck } from "../../components/Icons";

const ACCENT = "#4D94FF";

const FAQS = [
  {
    question: "O que precisa ser instalado nos computadores?",
    answer:
      "Instalamos um agente RMM leve nos computadores — um pequeno programa que roda em segundo plano, invisível para o usuário, e nos envia dados de saúde e status em tempo real. Não afeta a performance.",
  },
  {
    question: "O RMM é diferente de ter suporte técnico?",
    answer:
      "São complementares. O RMM é a camada de monitoramento e automação — a inteligência que detecta problemas. O suporte técnico é a equipe que age quando necessário. Juntos, garantem proatividade e resolução rápida.",
  },
  {
    question: "Posso ver os dispositivos da minha empresa?",
    answer:
      "Sim. Disponibilizamos acesso ao painel de monitoramento para que você tenha visibilidade do status dos dispositivos da empresa, se desejar.",
  },
];

const FEATURES = [
  {
    title: "Monitoramento em Tempo Real",
    desc: "CPU, memória, disco, processos e saúde de cada dispositivo monitorados continuamente",
  },
  {
    title: "Alertas Automáticos",
    desc: "Notificações imediatas quando algo sai do padrão — antes de virar problema",
  },
  {
    title: "Gestão de Patches",
    desc: "Windows Update, software de terceiros e atualizações de segurança aplicados automaticamente",
  },
  {
    title: "Inventário Completo",
    desc: "Hardware, software, licenças e configurações de cada dispositivo catalogados",
  },
  {
    title: "Acesso Remoto Seguro",
    desc: "Nossa equipe acessa os dispositivos remotamente para diagnóstico e resolução sem precisar ir ao local",
  },
  {
    title: "Relatórios de Endpoint",
    desc: "Relatórios periódicos de saúde, conformidade e status de todos os dispositivos",
  },
];

const MONITORING_ITEMS = [
  "Computadores e notebooks Windows/Mac",
  "Servidores físicos e virtuais",
  "Saúde de disco, RAM e CPU",
  "Status de serviços e processos críticos",
  "Atualizações pendentes e vulnerabilidades",
  "Softwares instalados e inventário",
  "Tempo de funcionamento e logs",
];

export default function RMM() {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq((prev) => (prev === index ? null : index));
  };

  return (
    <>
      {/* ═══════════════ ABERTURA ═══════════════ */}
      <section className="page-hero">
        <div className="page-hero__bg"></div>
        <div className="page-hero__grid"></div>
        <NetworkGraph variant="hero" className="page-hero__network" />
        <div className="container">
          <div className="breadcrumb animate-up">
            <Link to="/">Início</Link>
            <span>/</span>
            <Link to="/servicos/servicos">Serviços</Link>
            <span>/</span>
            <span>RMM / Endpoints</span>
          </div>
          <div className="tag animate-up animate-up--d1" style={{ color: ACCENT, background: "rgba(77,148,255,.12)", borderColor: `${ACCENT}40` }}>
            <IconMonitor style={{ width: "13px", height: "13px" }} />
            Remote Monitoring &amp; Management
          </div>
          <h1
            className="animate-up animate-up--d2"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(36px,5vw,64px)",
              fontWeight: 700,
              letterSpacing: "-.03em",
              lineHeight: "1.1",
              marginBottom: "24px",
              color: "var(--text-primary)",
            }}
          >
            Gestão de Endpoints
            <br />
            <span style={{ color: ACCENT }}>
              Controle total dos dispositivos
            </span>
          </h1>
          <p
            className="animate-up animate-up--d3"
            style={{
              fontSize: "18px",
              color: "var(--text-secondary)",
              maxWidth: "560px",
              lineHeight: "1.7",
              marginBottom: "40px",
            }}
          >
            Monitoramento e gerenciamento remoto de todos os computadores,
            servidores e dispositivos da sua empresa — visibilidade total,
            alertas em tempo real, sem precisar de alguém no local.
          </p>
          <div className="hero-actions-col animate-up animate-up--d4">
            <Link to="/contato" className="btn btn-primary">
              Solicitar proposta →
            </Link>
            <Link to="/servicos/servicos" className="btn btn-outline">
              Ver todos os serviços
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════ O QUE ESTÁ INCLUSO ═══════════════ */}
      <section className="section">
        <div className="container">
          <div className="grid-2col">
            <Reveal>
              <div className="tag" style={{ color: ACCENT, background: "rgba(77,148,255,.12)", borderColor: `${ACCENT}40` }}>
                O que está incluso
              </div>
              <h2 className="section-title" style={{ marginBottom: "28px" }}>
                O que a sepolTECH <span style={{ color: ACCENT }}>entrega</span>
              </h2>
              <div className="feature-list">
                {FEATURES.map((f) => (
                  <div className="feature-item" key={f.title}>
                    <div
                      className="feature-item__icon"
                      style={{ background: "rgba(77,148,255,.15)", color: ACCENT }}
                    >
                      ✓
                    </div>
                    <div>
                      <strong style={{ color: "var(--text-primary)" }}>
                        {f.title}
                      </strong>{" "}
                      — {f.desc}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div
                className="card"
                style={{ padding: "32px" }}
              >
                <h3
                  style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 700,
                    fontSize: "18px",
                    marginBottom: "20px",
                    color: "var(--text-primary)",
                  }}
                >
                  O que o RMM monitora
                </h3>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: "12px" }}
                >
                  {MONITORING_ITEMS.map((item) => (
                    <div className="feature-item" key={item}>
                      <div
                        className="feature-item__icon"
                        style={{
                          background: "rgba(77,148,255,.15)",
                          color: ACCENT,
                        }}
                      >
                        <IconCheck style={{ width: "10px", height: "10px" }} />
                      </div>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════════ FAQ ═══════════════ */}
      <section className="section section--alt">
        <div className="container" style={{ maxWidth: "780px" }}>
          <Reveal className="text-center" style={{ marginBottom: "40px" }}>
            <div className="tag" style={{ color: ACCENT, background: "rgba(77,148,255,.12)", borderColor: `${ACCENT}40` }}>
              FAQ
            </div>
            <h2 className="section-title">Dúvidas frequentes</h2>
          </Reveal>

          {FAQS.map((faq, index) => (
            <Reveal
              className={`faq-item${openFaq === index ? " open" : ""}`}
              delay={index * 0.05}
              key={faq.question}
            >
              <button
                className="faq-btn"
                onClick={() => toggleFaq(index)}
                aria-expanded={openFaq === index}
              >
                {faq.question}
                <span className="faq-btn__icon">+</span>
              </button>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══════════════ CTA ═══════════════ */}
      <section className="section">
        <div className="container">
          <Reveal className="cta-banner">
            <h2 className="cta-banner__title">
              Visibilidade total
              <br />
              dos dispositivos da sua empresa
            </h2>
            <p className="cta-banner__sub">
              Chega de descobrir problemas quando já é tarde demais. Monitore
              tudo com a sepolTECH.
            </p>
            <div className="cta-banner__actions">
              <Link to="/contato" className="btn btn-primary">
                Contratar RMM →
              </Link>
              <Link to="/servicos/servicos" className="btn btn-outline">
                Ver todos os serviços
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
